import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext/CookieConsentContext';

// Replace with your actual Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-CFGZ2PFLM4';

/**
 * Hook to initialize Google Analytics and track page views
 * Only loads GA if user has given consent for analytics cookies
 */
export const useGoogleAnalytics = () => {
  const { analyticsAllowed, isLoaded } = useCookieConsent();
  const location = useLocation();
  const isInitialized = useRef(false);

  // Initialize GA when consent is given
  useEffect(() => {
    if (!isLoaded || !analyticsAllowed || isInitialized.current) {
      return;
    }

    // Check if GA is already loaded
    if (window.gtag) {
      isInitialized.current = true;
      return;
    }

    // Load GA script dynamically
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
      send_page_view: false, // We'll send page views manually
      anonymize_ip: true, // GDPR compliance
    });

    isInitialized.current = true;

    // Track initial page view
    trackPageView(location.pathname);
  }, [isLoaded, analyticsAllowed, location.pathname]);

  // Track page views on route change
  useEffect(() => {
    if (!isInitialized.current || !analyticsAllowed) {
      return;
    }

    trackPageView(location.pathname);
  }, [location.pathname, analyticsAllowed]);

  return {
    isInitialized: isInitialized.current,
    trackEvent,
    trackPageView,
  };
};

/**
 * Track a page view
 * @param {string} path - Page path
 */
const trackPageView = (path) => {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: document.title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - Event name
 * @param {Object} params - Event parameters
 */
const trackEvent = (eventName, params = {}) => {
  if (!window.gtag) return;

  window.gtag('event', eventName, params);
};

export { trackEvent, trackPageView };
